import Dialog from "@material-ui/core/esm/Dialog";
import DialogTitle from "@material-ui/core/esm/DialogTitle";
import DialogActions from "@material-ui/core/esm/DialogActions";
import DialogContent from "@material-ui/core/esm/DialogContent";
import CloseIcon from '@material-ui/icons/Close';
import axios from 'axios';
import { MuiTelInput } from 'mui-tel-input';
import { nanoid } from 'nanoid';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomerService } from 'api';
import { PrimaryButton, SecondaryButton } from 'components/Button';
import Divider from 'components/Divider/Divider';
import { FormElement } from 'components/Form';
import { Title } from 'components/Text';
import { BLACK } from 'constants/colors';
import { DEFAULT_LANGUAGE } from 'constants/language';
import useCurrentCustomer from 'hooks/customer/useCurrentCustomer';
import showPhoneNumberError from 'utils/form/showPhoneNumberError';
import isNorwegian from 'utils/language/isNorwegian';
import getPhoneNumberError from 'utils/phone/getPhoneNumberError';
import isValidPhoneNumber from 'utils/phone/isValidPhoneNumber';
import { getPhoneNumberValue } from 'utils/phone/parsePhoneNumber';
import { trimAllSpaces } from 'utils/text/trimWhitespace';
import useStyles from '../ProfileStyles';
const idSuffix = nanoid();
export default function PhoneDialog({
  open,
  onClose,
  phoneNumber,
  fieldName
}) {
  const [isLoading, setIsLoading] = useState(false);
  const {
    t,
    i18n
  } = useTranslation();
  const {
    customer,
    setCustomer
  } = useCurrentCustomer();
  const isNorwegianLangSet = isNorwegian(i18n);
  const [phoneNumberInput, setPhoneNumberInput] = useState(getPhoneNumberValue(phoneNumber));
  useEffect(() => {
    setPhoneNumberInput(getPhoneNumberValue(phoneNumber));
  }, [phoneNumber]);
  const {
    dialog,
    dialogTitle,
    closeIcon,
    submitButton,
    textField
  } = useStyles();
  const {
    enqueueSnackbar
  } = useSnackbar();
  const submitPhone = async () => {
    if (customer) {
      try {
        setIsLoading(true);
        const trimmedPhoneNumber = trimAllSpaces(phoneNumberInput);
        if (trimmedPhoneNumber && customer.id) {
          const customerBody = {
            ...customer,
            [fieldName]: trimmedPhoneNumber
          };
          const customerObject = await CustomerService.editCustomer({
            customerId: customer.id,
            body: customerBody
          });
          setCustomer(customerObject);
        } else {
          console.error('Could not find customer id', customer.id);
        }
      } catch (e) {
        if (!axios.isCancel(e)) enqueueSnackbar(t('error.edit_phone_nr'), {
          variant: 'error'
        });
      } finally {
        setIsLoading(false);
        onClose();
      }
    }
  };
  const isInputValid = isValidPhoneNumber(phoneNumberInput ?? '');
  const showPhoneError = showPhoneNumberError(phoneNumberInput ?? '');
  return /*#__PURE__*/React.createElement(Dialog, {
    open: open,
    onClose: onClose,
    "aria-labelledby": "telephone-dialog-form"
  }, /*#__PURE__*/React.createElement(DialogTitle, {
    id: "telephone-dialog-form"
  }, /*#__PURE__*/React.createElement("div", {
    className: dialogTitle
  }, /*#__PURE__*/React.createElement(Title, {
    component: "h1"
  }, t('profile.edit_telephone')), /*#__PURE__*/React.createElement(SecondaryButton, {
    className: closeIcon,
    ariaLabel: "close dialog",
    onClick: () => {
      onClose();
      setPhoneNumberInput(getPhoneNumberValue(phoneNumber));
    }
  }, /*#__PURE__*/React.createElement(CloseIcon, {
    htmlColor: BLACK
  })))), /*#__PURE__*/React.createElement(Divider, null), /*#__PURE__*/React.createElement(DialogContent, {
    className: dialog
  }, /*#__PURE__*/React.createElement(FormElement, {
    title: t('profile.telephone'),
    childId: `phoneNumber_${idSuffix}`
  }, /*#__PURE__*/React.createElement("div", {
    className: textField
  }, /*#__PURE__*/React.createElement(MuiTelInput, {
    name: "phoneNumber",
    onChange: (_, info) => setPhoneNumberInput(info.numberValue || ''),
    variant: "outlined",
    defaultCountry: DEFAULT_LANGUAGE,
    langOfCountryName: isNorwegianLangSet ? 'NO' : 'EN',
    fullWidth: true,
    forceCallingCode: true,
    value: phoneNumberInput,
    error: showPhoneError,
    helperText: showPhoneError ? getPhoneNumberError(phoneNumberInput ?? '', t) : t('profile.phone_number_info'),
    inputProps: {
      id: `phoneNumber_${idSuffix}`,
      maxLength: 17
    }
  })))), /*#__PURE__*/React.createElement(DialogActions, null, /*#__PURE__*/React.createElement("div", {
    className: submitButton
  }, /*#__PURE__*/React.createElement(PrimaryButton, {
    onClick: submitPhone,
    disabled: !isInputValid,
    loading: isLoading
  }, t('buttons.save_edits')))));
}